import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ArrowRight, CalendarClock, Search } from "lucide-react";
import { BOOKING_STATUS_OPTIONS } from "../config";
import { subscribePhoenixBookings, subscribePhoenixSpaces } from "../service";
import {
  AdminPageHeader,
  displaySpaceNames,
  formatTimestamp,
  PhoenixAdminNav,
  StatusBadge,
} from "./AdminShared";

export default function PhoenixBookings() {
  const [bookings, setBookings] = useState([]);
  const [spaces, setSpaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({ status: "", space: "", bookingType: "", conflict: "", dateFrom: "", dateTo: "", search: "" });

  useEffect(() => {
    const unsubscribeBookings = subscribePhoenixBookings(
      (rows) => { setBookings(rows); setLoading(false); },
      () => { setError("Bookings could not be loaded. Check your administrator access and connection."); setLoading(false); }
    );
    const unsubscribeSpaces = subscribePhoenixSpaces(setSpaces, () => {});
    return () => { unsubscribeBookings(); unsubscribeSpaces(); };
  }, []);

  const statusCounts = useMemo(() => Object.fromEntries(BOOKING_STATUS_OPTIONS.map(([status]) => [status, bookings.filter((booking) => booking.status === status).length])), [bookings]);

  const filtered = useMemo(() => {
    const needle = filters.search.trim().toLowerCase();
    return bookings.filter((booking) => {
      const searchable = [booking.bookingReference, booking.contact?.fullName, booking.contact?.email, booking.contact?.telephone, booking.organisation?.name, booking.activityDetails?.name].join(" ").toLowerCase();
      const firstDate = booking.requestedDates?.[0]?.date || "";
      return (!filters.status || booking.status === filters.status)
        && (!filters.space || booking.selectedSpaceIds?.includes(filters.space))
        && (!filters.bookingType || booking.bookingType === filters.bookingType)
        && (!filters.conflict || booking.conflictStatus === filters.conflict)
        && (!filters.dateFrom || firstDate >= filters.dateFrom)
        && (!filters.dateTo || firstDate <= filters.dateTo)
        && (!needle || searchable.includes(needle));
    });
  }, [bookings, filters]);

  const updateFilter = (event) => setFilters((current) => ({ ...current, [event.target.name]: event.target.value }));

  return (
    <div className="phx-shell phx-admin-shell">
      <PhoenixAdminNav />
      <AdminPageHeader title="Phoenix Hub bookings"><p>Review requests, resolve conflicts and move each booking through approval, payment and confirmation.</p></AdminPageHeader>

      <section className="phx-admin-stats" aria-label="Booking totals">
        {BOOKING_STATUS_OPTIONS.slice(0, 6).map(([status, label]) => <button type="button" key={status} onClick={() => setFilters((current) => ({ ...current, status }))}><strong>{statusCounts[status] || 0}</strong><span>{label}</span></button>)}
      </section>

      <section className="phx-admin-filters" aria-label="Booking filters">
        <label className="phx-admin-search"><Search aria-hidden="true" /><input name="search" value={filters.search} onChange={updateFilter} placeholder="Search name, organisation, contact, activity or reference" /></label>
        <select name="status" value={filters.status} onChange={updateFilter} aria-label="Filter by status"><option value="">All statuses</option>{BOOKING_STATUS_OPTIONS.map(([value, label]) => <option value={value} key={value}>{label}</option>)}</select>
        <select name="space" value={filters.space} onChange={updateFilter} aria-label="Filter by space"><option value="">All spaces</option>{spaces.map((space) => <option value={space.id} key={space.id}>{space.name}</option>)}</select>
        <select name="bookingType" value={filters.bookingType} onChange={updateFilter} aria-label="Filter by booking type"><option value="">All booking types</option><option value="one_off">One-off</option><option value="recurring">Recurring</option><option value="multi_day">Multi-day</option><option value="general_enquiry">General enquiry</option></select>
        <select name="conflict" value={filters.conflict} onChange={updateFilter} aria-label="Filter by conflict"><option value="">All conflict states</option><option value="clear">Clear</option><option value="possible_conflict">Possible conflict</option><option value="confirmed_conflict">Confirmed conflict</option></select>
        <label>From<input type="date" name="dateFrom" value={filters.dateFrom} onChange={updateFilter} /></label>
        <label>To<input type="date" name="dateTo" value={filters.dateTo} onChange={updateFilter} /></label>
        <button type="button" className="phx-admin-clear" onClick={() => setFilters({ status: "", space: "", bookingType: "", conflict: "", dateFrom: "", dateTo: "", search: "" })}>Clear filters</button>
      </section>

      {error ? <div className="phx-submit-error" role="alert">{error}</div> : null}
      {loading ? <p className="phx-loading" role="status">Loading booking requests…</p> : filtered.length === 0 ? (
        <div className="phx-admin-empty"><CalendarClock aria-hidden="true" /><h2>No booking requests match</h2><p>Try clearing filters, or check back when a resident submits a request.</p></div>
      ) : (
        <div className="phx-admin-table-wrap">
          <table className="phx-admin-table">
            <thead><tr><th>Reference</th><th>Contact and activity</th><th>Space and date</th><th>Status</th><th><span className="phx-sr-only">Open</span></th></tr></thead>
            <tbody>{filtered.map((booking) => <tr key={booking.id}><td><strong>{booking.bookingReference}</strong><small>{formatTimestamp(booking.submittedAt)}</small></td><td><strong>{booking.contact?.fullName || "—"}</strong><small>{booking.organisation?.name || booking.activityDetails?.name || "No organisation"}</small></td><td><strong>{displaySpaceNames(booking.selectedSpaceIds, spaces)}</strong><small>{booking.requestedDates?.[0]?.date || "Dates to discuss"}{booking.bookingType === "recurring" ? " · Recurring" : ""}</small></td><td><StatusBadge status={booking.status} />{booking.conflictStatus === "possible_conflict" ? <span className="phx-conflict"><AlertTriangle aria-hidden="true" /> Possible conflict</span> : null}</td><td><Link aria-label={`Open ${booking.bookingReference}`} to={`/admin/phoenix-bookings/${booking.id}`}><ArrowRight aria-hidden="true" /></Link></td></tr>)}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}

